import { Fragment } from 'react';
import { useQuery, useMutation } from '@apollo/client/react';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import Loading from '../loading';
import ErrorMessage from '../error-message';
import NotFound from '../not-found';
import InviteList from '../invite-list';
import {
  groupInvitesQuery,
  expireGroupInviteMutation,
  extendGroupInviteMutation,
  resendGroupEmailInviteMutation
} from '../../apollo/groups';
import NewLinkInvite from './new-link-invite';
import NewEmailInvites from './new-email-invites';
import CopyInput from '../copy-input';
import type { Group } from '../../types/Group';
import config from '../../config';

type Props = {
  group?: Group;
};

const GroupInvitesTab = ({ group: groupProp }: Props) => {
  const { data, loading, error } = useQuery(groupInvitesQuery, {
    variables: {
      slug: groupProp.slug
    }
  });
  const group = data?.group;

  const [expireGroupInvite] = useMutation(expireGroupInviteMutation);
  const [extendGroupInvite] = useMutation(extendGroupInviteMutation);
  const [resendGroupEmailInvite] = useMutation(resendGroupEmailInviteMutation);

  if (error) {
    return <ErrorMessage message='An error occurred while loading invites.' error={error} compact />;
  }
  if (loading) { return <Loading compact />; }
  if (!group || !group.permissions.editable) { return <NotFound compact />; }

  const inviteListProps = {
    basePath: `/groups/${group.slug}`,
    expireInvite: expireGroupInvite,
    extendInvite: extendGroupInvite,
    resendEmailInvite: resendGroupEmailInvite
  };

  return (
    <Fragment>
      {group.public && (
        <Alert severity="info" icon={false} sx={{ my: 2 }}>
          <p style={{ marginTop: 0 }}>Special invites are not required for public groups. Anyone with a link to the group can join.</p>
          <CopyInput text={`${config().universal.baseUri}/groups/${group.slug}`} />
        </Alert>
      )}

      <Typography variant="h2">Link Invites</Typography>
      <InviteList invites={group.linkInvites} {...inviteListProps} />
      <NewLinkInvite group={group} />

      <Typography variant="h2">Email Invites</Typography>
      <InviteList invites={group.emailInvites} {...inviteListProps} />
      <NewEmailInvites group={group} />
    </Fragment>
  );
};

export default GroupInvitesTab;
